import React, { useState, useCallback, useRef, memo } from "react";
import {
  Box,
  IconButton,
  Menu,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import CodeIcon from "@mui/icons-material/Code";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";

function WabaTextMsg({
  label = "Body Text",
  charLimit = 1024,
  value: controlledValue,
  onChange, // (text) => void
  error,
  helperText,
  ...props
}) {
  /* -------- controlled vs internal -------- */
  const [internalValue, setInternalValue] = useState("");
  const isControlledValue = controlledValue !== undefined;
  const value = isControlledValue ? controlledValue : internalValue;
  const setValue = isControlledValue ? () => {} : setInternalValue;

  const safeCharLimit = Math.max(10, charLimit);

  /* -------- refs -------- */
  const textFieldRef = useRef(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);

  /* -------- push helper -------- */
  const pushValue = (nextVal) => {
    if (isControlledValue && onChange) onChange(nextVal);
    else setInternalValue(nextVal);
  };

  /* -------- handlers -------- */
  const handleTextChange = (e) => {
    const next = e.target.value.slice(0, safeCharLimit);
    pushValue(next);
  };

  const insertSnippet = (snippet, cursorBack = 0) => {
    const newText = `${value}${snippet}`;
    if (newText.length > safeCharLimit) return;
    pushValue(newText);

    setTimeout(() => {
      const input =
        textFieldRef.current?.querySelector("textarea") ||
        textFieldRef.current?.querySelector("input");
      if (input) {
        const pos = newText.length - cursorBack;
        input.focus();
        input.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleFormatClick = useCallback(
    (format) => {
      if (value.length >= safeCharLimit) return;
      let newText = "";
      let cursorPos = 0;
      switch (format) {
        case "bold":
          newText = `${value}**`;
          cursorPos = value.length + 1;
          break;
        case "strikethrough":
          newText = `${value}~~`;
          cursorPos = value.length + 1;
          break;
        case "monospace":
          newText = `${value}\`\`\`\`\`\``;
          cursorPos = value.length + 3;
          break;
        case "italic":
          newText = `${value}__`;
          cursorPos = value.length + 1;
          break;
        default:
          return;
      }

      if (newText.length <= safeCharLimit) {
        pushValue(newText);
        setTimeout(() => {
          if (textFieldRef.current) {
            const input =
              textFieldRef.current.querySelector("textarea") ||
              textFieldRef.current.querySelector("input");
            if (input) {
              input.focus();
              input.setSelectionRange(cursorPos, cursorPos);
            }
          }
        }, 0);
      }
    },
    [value, safeCharLimit]
  );

  const handleEmojiSelect = (em) => {
    insertSnippet(em, 0);
    // setEmojiAnchorEl(null);
  };

  /* -------- helper text -------- */
  const len = value.length;
  const helper =
    `${len}/${safeCharLimit}` +
    (len === safeCharLimit
      ? " – limit reached"
      : len >= safeCharLimit - 10
      ? " – approaching"
      : "");

  /* ===================== JSX ===================== */
  return (
    <Box {...props}>
      <TextField
        fullWidth
        multiline
        rows={4}
        value={value}
        onChange={handleTextChange}
        inputProps={{ maxLength: safeCharLimit }}
        placeholder={label}
        ref={textFieldRef}
        error={len === safeCharLimit || error}
        helperText={
          <Typography
            variant="caption"
            color={
              len === safeCharLimit
                ? "error"
                : len >= safeCharLimit - 10
                ? "warning"
                : "textSecondary"
            }
          >
            {helperText || helper}
          </Typography>
        }
      />

      {/* toolbar */}
      <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mt: -2 }}>
        <Tooltip title="Bold">
          <IconButton
            size="small"
            disabled={value.length >= safeCharLimit}
            onClick={() => handleFormatClick("bold")}
          >
            <FormatBoldIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Strikethrough">
          <IconButton
            size="small"
            disabled={value.length >= safeCharLimit}
            onClick={() => handleFormatClick("strikethrough")}
          >
            <StrikethroughSIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Monospace">
          <IconButton
            size="small"
            disabled={value.length >= safeCharLimit}
            onClick={() => handleFormatClick("monospace")}
          >
            <CodeIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic">
          <IconButton
            size="small"
            disabled={value.length >= safeCharLimit}
            onClick={() => handleFormatClick("italic")}
          >
            <FormatItalicIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Insert Emoji">
          <IconButton
            size="small"
            disabled={value.length >= safeCharLimit}
            onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
          >
            <EmojiEmotionsIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={emojiAnchorEl}
          open={Boolean(emojiAnchorEl)}
          onClose={() => setEmojiAnchorEl(null)}
        >
          <Picker
            data={emojiData}
            onEmojiSelect={(emoji) => handleEmojiSelect(emoji.native)}
            maxFrequentRows={0}
            perLine={8}
            skinTonePosition="search"
            theme="auto"
            previewPosition="none"
          />
        </Menu>
      </Box>
    </Box>
  );
}

export default memo(WabaTextMsg);
